import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './occupation.reducer';

export const Occupation = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const occupationList = useAppSelector(state => state.occupation.entities);
  const loading = useAppSelector(state => state.occupation.loading);
  const totalItems = useAppSelector(state => state.occupation.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="occupation-heading" data-cy="OccupationHeading">
        <Translate contentKey="apiLocationApp.occupation.home.title">Occupations</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.occupation.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/occupation/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.occupation.home.createLabel">Create new Occupation</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {occupationList && occupationList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.occupation.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.occupation.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.occupation.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.occupation.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.occupation.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllUsualResidents')}>
                  <Translate contentKey="apiLocationApp.occupation.totalAllUsualResidents">Total All Usual Residents</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllUsualResidents')} />
                </th>
                <th className="hand" onClick={sort('managersDirectorsOfficials')}>
                  <Translate contentKey="apiLocationApp.occupation.managersDirectorsOfficials">Managers Directors Officials</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('managersDirectorsOfficials')} />
                </th>
                <th className="hand" onClick={sort('professionalOccupations')}>
                  <Translate contentKey="apiLocationApp.occupation.professionalOccupations">Professional Occupations</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('professionalOccupations')} />
                </th>
                <th className="hand" onClick={sort('associateProfessionalTechnical')}>
                  <Translate contentKey="apiLocationApp.occupation.associateProfessionalTechnical">
                    Associate Professional Technical
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('associateProfessionalTechnical')} />
                </th>
                <th className="hand" onClick={sort('administrativeSecretarial')}>
                  <Translate contentKey="apiLocationApp.occupation.administrativeSecretarial">Administrative Secretarial</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('administrativeSecretarial')} />
                </th>
                <th className="hand" onClick={sort('skilledTradesOccupations')}>
                  <Translate contentKey="apiLocationApp.occupation.skilledTradesOccupations">Skilled Trades Occupations</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('skilledTradesOccupations')} />
                </th>
                <th className="hand" onClick={sort('caringLeisureService')}>
                  <Translate contentKey="apiLocationApp.occupation.caringLeisureService">Caring Leisure Service</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('caringLeisureService')} />
                </th>
                <th className="hand" onClick={sort('salesCustomerService')}>
                  <Translate contentKey="apiLocationApp.occupation.salesCustomerService">Sales Customer Service</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('salesCustomerService')} />
                </th>
                <th className="hand" onClick={sort('processPlantMachineOperatives')}>
                  <Translate contentKey="apiLocationApp.occupation.processPlantMachineOperatives">
                    Process Plant Machine Operatives
                  </Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('processPlantMachineOperatives')} />
                </th>
                <th className="hand" onClick={sort('elementaryOccupations')}>
                  <Translate contentKey="apiLocationApp.occupation.elementaryOccupations">Elementary Occupations</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('elementaryOccupations')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {occupationList.map((occupation, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/occupation/${occupation.id}`} color="link" size="sm">
                      {occupation.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${occupation.geographicLevel}`} />
                  </td>
                  <td>{occupation.date}</td>
                  <td>{occupation.geography}</td>
                  <td>{occupation.geographyCode}</td>
                  <td>{occupation.totalAllUsualResidents}</td>
                  <td>{occupation.managersDirectorsOfficials}</td>
                  <td>{occupation.professionalOccupations}</td>
                  <td>{occupation.associateProfessionalTechnical}</td>
                  <td>{occupation.administrativeSecretarial}</td>
                  <td>{occupation.skilledTradesOccupations}</td>
                  <td>{occupation.caringLeisureService}</td>
                  <td>{occupation.salesCustomerService}</td>
                  <td>{occupation.processPlantMachineOperatives}</td>
                  <td>{occupation.elementaryOccupations}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/occupation/${occupation.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/occupation/${occupation.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/occupation/${occupation.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.occupation.home.notFound">No Occupations found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={occupationList && occupationList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Occupation;
